import * as React from 'react';
import { cn } from './cn';

export type BadgeVariant =
  | 'neutral'
  | 'accent'
  | 'saffron'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'outline';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: 'sm' | 'md';
  /** Leading dot — good for statuses (queued / rendering / done). */
  dot?: boolean;
  icon?: React.ReactNode;
}

const BADGE_VARIANT: Record<BadgeVariant, string> = {
  neutral: 'bg-paper-3 text-ink-2',
  accent: 'bg-accent-soft text-accent',
  saffron: 'bg-saffron-soft text-warning',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  info: 'bg-info-soft text-info',
  outline: 'border border-line-strong text-ink-2',
};

/** Small status pill. Non-interactive — use Button for anything clickable. */
export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { variant = 'neutral', size = 'sm', dot, icon, className, children, ...props },
  ref,
) {
  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full font-medium whitespace-nowrap',
        size === 'sm' ? 'text-2xs h-5 px-2' : 'h-6 px-2.5 text-xs',
        BADGE_VARIANT[variant],
        className,
      )}
      {...props}
    >
      {dot ? <span className="size-1.5 shrink-0 rounded-full bg-current" aria-hidden /> : null}
      {icon ? <span className="[&>svg]:size-3.5 shrink-0">{icon}</span> : null}
      {children}
    </span>
  );
});
